import { z } from 'zod';
import { sha256 } from '@noble/hashes/sha256';

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/graphql/client.ts
var GraphQLRequestError = class extends Error {
  constructor(message, errors = [], status = 0, correlationId) {
    super(message);
    this.errors = errors;
    this.status = status;
    this.correlationId = correlationId;
    this.name = "GraphQLRequestError";
  }
  errors;
  status;
  correlationId;
  static {
    brandClass(this, "graphql/GraphQLRequestError");
  }
};
var CORRELATION_ID_HEADERS = [
  // AppSync's own id for this request — present on success and on failure.
  "x-amzn-RequestId",
  // X-Ray, when tracing is enabled on the API.
  "x-amzn-Trace-Id",
  // CloudFront, when the endpoint is fronted by it.
  "x-amz-cf-id",
  "x-request-id"
];
var CORRELATION_ID_EXTENSION_KEYS = [
  "correlationId",
  "correlation_id",
  "requestId",
  "request_id"
];
var OPAQUE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:=+_-]{7,127}$/;
function isOpaqueRequestId(value) {
  return typeof value === "string" && OPAQUE_ID_RE.test(value) && !/arn:/i.test(value);
}
function correlationIdFrom(response, errors) {
  for (const error of errors) {
    const extensions = error?.extensions;
    if (!extensions || typeof extensions !== "object") continue;
    for (const key of CORRELATION_ID_EXTENSION_KEYS) {
      const value = extensions[key];
      if (isOpaqueRequestId(value)) return value;
    }
  }
  const headers = response?.headers;
  if (!headers || typeof headers.get !== "function") return void 0;
  for (const name of CORRELATION_ID_HEADERS) {
    const value = headers.get(name);
    if (isOpaqueRequestId(value)) return value;
  }
  return void 0;
}
function isGraphQLRequestError(err) {
  return err instanceof GraphQLRequestError;
}
var DEFAULT_TIMEOUT_MS = 15e3;
var GraphQLClient = class {
  static {
    brandClass(this, "graphql/GraphQLClient");
  }
  config;
  doFetch;
  /** See {@link onError}. */
  errorListeners = /* @__PURE__ */ new Set();
  constructor(config) {
    this.config = config;
    const impl = config.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : void 0);
    if (!impl) throw new Error("GraphQLClient: no fetch implementation available");
    this.doFetch = impl.bind(globalThis);
  }
  /**
   * Be told about every request that fails, whoever made it. The GraphQL twin of
   * `ApiClient.onErrorEnvelope`, for the same reason: a plan refusal (`PERM003`)
   * means the host's copy of the plan is stale, and the read that met it sits
   * behind any of a dozen adapters — most of which fall back to REST and never
   * say. A listener that throws is skipped: an observer must not become the
   * request's failure. Returns an unsubscribe.
   */
  onError(listener) {
    this.errorListeners.add(listener);
    return () => {
      this.errorListeners.delete(listener);
    };
  }
  /**
   * Execute one GraphQL operation and return its typed `data`. Throws
   * {@link GraphQLRequestError} on any transport failure or GraphQL `errors[]`.
   * Caller cancellation preserves the signal's reason and is not a failure.
   */
  async request(query, variables, opts = {}) {
    try {
      return await this.send(query, variables, opts);
    } catch (err) {
      opts.signal?.throwIfAborted();
      if (err instanceof GraphQLRequestError) {
        for (const listener of [...this.errorListeners]) {
          try {
            listener(err);
          } catch {
          }
        }
      }
      throw err;
    }
  }
  async send(query, variables, opts) {
    opts.signal?.throwIfAborted();
    const controller = new AbortController();
    const timeoutMs = opts.timeoutMs ?? this.config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const onAbort = () => controller.abort(opts.signal?.reason);
    let rejectCancelled;
    const cancelled = new Promise((_resolve, reject) => {
      rejectCancelled = () => reject(controller.signal.reason);
      controller.signal.addEventListener("abort", rejectCancelled, { once: true });
    });
    opts.signal?.addEventListener("abort", onAbort, { once: true });
    const timer = setTimeout(() => controller.abort(
      new GraphQLRequestError("GraphQL request timed out", [], 0)
    ), timeoutMs);
    try {
      return await Promise.race([
        this.sendOperation(query, variables, controller.signal),
        cancelled
      ]);
    } finally {
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
      controller.signal.removeEventListener("abort", rejectCancelled);
    }
  }
  async sendOperation(query, variables, signal) {
    signal.throwIfAborted();
    const endpoint = typeof this.config.endpoint === "function" ? this.config.endpoint() : this.config.endpoint;
    if (!endpoint) {
      throw new GraphQLRequestError("GraphQL endpoint is not resolvable", [], 0);
    }
    const token = await Promise.resolve(this.config.getAccessToken());
    signal.throwIfAborted();
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    if (token) headers.Authorization = token;
    if (this.config.requestSigner) {
      Object.assign(headers, await Promise.resolve(this.config.requestSigner()));
    }
    signal.throwIfAborted();
    let response;
    try {
      response = await this.doFetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables: variables ?? {} }),
        signal
      });
    } catch (err) {
      signal.throwIfAborted();
      throw new GraphQLRequestError(
        err instanceof Error ? err.message : "GraphQL request failed",
        [],
        0
      );
    }
    signal.throwIfAborted();
    if (!response.ok) {
      const body2 = await safeJson(response);
      signal.throwIfAborted();
      const errors2 = extractErrors(body2);
      throw new GraphQLRequestError(
        errors2[0]?.message ?? `GraphQL request failed with status ${response.status}`,
        errors2,
        response.status,
        correlationIdFrom(response, errors2)
      );
    }
    const body = await safeJson(response);
    signal.throwIfAborted();
    const errors = extractErrors(body);
    const firstError = errors[0];
    if (firstError) {
      throw new GraphQLRequestError(
        firstError.message,
        errors,
        response.status,
        correlationIdFrom(response, errors)
      );
    }
    if (!body || body.data == null) {
      throw new GraphQLRequestError(
        "GraphQL response had no data",
        [],
        response.status,
        correlationIdFrom(response, [])
      );
    }
    return body.data;
  }
};
async function safeJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
function extractErrors(body) {
  if (body && typeof body === "object" && Array.isArray(body.errors)) {
    return body.errors;
  }
  return [];
}

// src/graphql/operations/catalog.ts
var KIND_TO_GRAPHQL = {
  tag: "TAG",
  label: "LABEL",
  entity: "ENTITY",
  group: "GROUP"
};
function catalogKindToGraphql(kind) {
  return KIND_TO_GRAPHQL[kind];
}
var CATALOG_ITEM_FIELDS = `
  id
  createdBy
  createdAt
  updatedBy
  updatedAt
  kind
  name
  description
  color
  icon
  logo
  category
  entityType
  website
  twitter
  riskLevel
  severity
  lifecycle
  confidence
  confidenceStatus
  explanation
  systemDefault
  aliases
  address
  chain
  chains
  primaryChains
  groupType
  sagaKind
  state
  memberCount
  withheldByPolicy
  rung
  editable
  presentationOverride
`;
var CATALOG_ITEMS_QUERY = (
  /* GraphQL */
  `
  query CatalogItems(
    $kind: CatalogKind!
    $limit: Int
    $includeDefaults: Boolean
    $search: String
    $cursor: String
  ) {
    catalogItems(
      kind: $kind
      limit: $limit
      includeDefaults: $includeDefaults
      search: $search
      cursor: $cursor
    ) {
      items { ${CATALOG_ITEM_FIELDS} }
      authorableRungs
      nextCursor
      hasMore
      approxTotal
      approxTotalCapped
      confidencePolicy { minConfidence includeUnscored confidenceReview active }
    }
  }
`
);
var clean = (v) => v == null ? void 0 : v;
function mapCatalogItemToRow(item) {
  return {
    id: item.id,
    created_by: clean(item.createdBy),
    created_at: clean(item.createdAt),
    updated_by: clean(item.updatedBy),
    updated_at: clean(item.updatedAt),
    name: clean(item.name),
    description: clean(item.description),
    color: clean(item.color),
    // tag/label/group carry the logo under `icon`; entity/address under
    // `logo_s3_uri` — the unified GraphQL `logo` feeds the latter.
    icon: clean(item.icon),
    logo_s3_uri: clean(item.logo),
    category: clean(item.category),
    // The entity's identity, under the snake_case names `rowToDraft` reads. Without
    // these the editor drafts an entity with the type, site and handle blank — and
    // `draftToRowBody` then omits from the save what the form never showed. The
    // fields are all `rowToDraft` looks at, so this is the whole fix client-side.
    entity_type: clean(item.entityType),
    website: clean(item.website),
    twitter: clean(item.twitter),
    risk_level: clean(item.riskLevel),
    severity: clean(item.severity),
    lifecycle: clean(item.lifecycle),
    confidence: clean(item.confidence),
    confidence_status: clean(item.confidenceStatus),
    withheld_by_policy: clean(item.withheldByPolicy),
    explanation: item.explanation ?? [],
    system_default: item.systemDefault,
    aliases: item.aliases ?? [],
    // Address-scoped rows (address labels) carry their binding; the REST list
    // returns these too, so the transport-swapped row stays shape-identical.
    address: clean(item.address),
    chain: clean(item.chain),
    // Applicability scope so the list chip ("Applies to chains" / "All chains")
    // survives a refetch. The REST list carries this as `applies_to_chains`; the
    // adapter normalises it onto the same `chains` field so both transports emit
    // a shape-identical row.
    chains: item.chains ?? [],
    // Presentation hint so the editor re-shows a saved "Primary chains" pick
    // after a reload (REST returns `primary_chains` directly; `rowToDraft` reads
    // this snake_case field).
    primary_chains: item.primaryChains ?? [],
    // The merged group view. A cross-chain saga the grouping engine found reaches
    // the list beside the groups a person curated, and `group_type` is how the
    // row renders as one rather than as a curated group with a broken name. The
    // saga fields are what the badge, leg count and state chip read; REST returns
    // them under exactly these names, so a transport swap changes nothing.
    group_type: clean(item.groupType),
    saga_kind: clean(item.sagaKind),
    state: clean(item.state),
    member_count: clean(item.memberCount),
    // The REST dump's names and spelling (a lower-case rung), so ownership reads
    // one shape off either transport and neither the badge nor the Edit gate
    // depends on which one won.
    rung: clean(item.rung?.toLowerCase()),
    editable: item.editable ?? void 0,
    presentation_override: awsJsonObject(item.presentationOverride)
  };
}
function awsJsonObject(value) {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return void 0;
    }
  }
  return parsed != null && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : void 0;
}
function mapCatalogItems(data) {
  const page = data.catalogItems;
  return {
    items: (page?.items ?? []).map(mapCatalogItemToRow),
    authorableRungs: page?.authorableRungs?.map((r) => r.toLowerCase()) ?? void 0,
    nextCursor: page?.nextCursor ?? void 0,
    hasMore: page?.hasMore ?? void 0,
    total: page?.approxTotal ?? void 0,
    totalCapped: page?.approxTotalCapped ?? void 0,
    confidencePolicy: page?.confidencePolicy ? {
      min_confidence: page.confidencePolicy.minConfidence,
      include_unscored: page.confidencePolicy.includeUnscored,
      confidence_review: page.confidencePolicy.confidenceReview,
      active: page.confidencePolicy.active
    } : void 0
  };
}
function catalogListUnroutable(kind, query = {}) {
  if (query.minConfidence !== void 0 || query.includeUnscored !== void 0 || query.confidenceReview !== void 0) return "Confidence filters use REST";
  if (catalogKindToGraphql(kind) == null) return `kind "${kind}"`;
  if (query.chain) return "chain filter";
  if (query.lifecycle) return "lifecycle filter";
  if (query.groupType) return "group-type filter";
  if (query.sagaKind) return "saga-kind filter";
  return null;
}
async function fetchCatalogItems(client, kind, query = {}, opts) {
  const graphqlKind = catalogKindToGraphql(kind);
  if (!graphqlKind) throw new Error(`catalog kind "${kind}" is not served by GraphQL`);
  const data = await client.request(
    CATALOG_ITEMS_QUERY,
    {
      kind: graphqlKind,
      limit: query.limit,
      includeDefaults: query.includeDefaults ?? true,
      search: query.search,
      cursor: query.cursor
    },
    opts
  );
  return mapCatalogItems(data);
}
var GRAPHQL_CATALOG_ID_LIMIT = 1;
var CATALOG_ITEM_QUERY = (
  /* GraphQL */
  `
  query CatalogItem($kind: CatalogKind!, $id: ID!) {
    catalogItem(kind: $kind, id: $id) { ${CATALOG_ITEM_FIELDS} }
  }
`
);
async function fetchCatalogItem(client, kind, id, opts) {
  const graphqlKind = catalogKindToGraphql(kind);
  if (!graphqlKind) throw new Error(`catalog kind "${kind}" is not served by GraphQL`);
  const data = await client.request(
    CATALOG_ITEM_QUERY,
    { kind: graphqlKind, id },
    opts
  );
  return data.catalogItem ? mapCatalogItemToRow(data.catalogItem) : null;
}
function catalogItemsByIdRequest(kind, ids) {
  const declarations = ["$kind: CatalogKind!"];
  const fields = [];
  const variables = { kind };
  ids.forEach((id, i) => {
    declarations.push(`$i${i}: ID!`);
    variables[`i${i}`] = id;
    fields.push(`r${i}: catalogItem(kind: $kind, id: $i${i}) { ${CATALOG_ITEM_FIELDS} }`);
  });
  return {
    query: `query CatalogItemsById(${declarations.join(", ")}) {
${fields.join("\n")}
}`,
    variables
  };
}
async function graphqlFetchCatalogItemsByIds(client, kind, ids, opts) {
  if (ids.length === 0) return [];
  const graphqlKind = catalogKindToGraphql(kind);
  if (!graphqlKind) throw new Error(`catalog kind "${kind}" is not served by GraphQL`);
  if (ids.length > GRAPHQL_CATALOG_ID_LIMIT) {
    throw new RangeError(
      `graphqlFetchCatalogItemsByIds: ${ids.length} ids exceeds ${GRAPHQL_CATALOG_ID_LIMIT}; use REST`
    );
  }
  const { query, variables } = catalogItemsByIdRequest(graphqlKind, ids);
  const data = await client.request(
    query,
    variables,
    opts
  );
  const out = [];
  ids.forEach((_id, i) => {
    const item = data[`r${i}`];
    if (item) out.push(mapCatalogItemToRow(item));
  });
  return out;
}

// src/graphql/operations/related.ts
var RELATED_SEED_KINDS = [
  "tag",
  "label",
  "entity",
  "group",
  "transaction_group"
];
var CATALOG_RELATED_QUERY = (
  /* GraphQL */
  `
  query CatalogRelated($kind: RelatedKind!, $id: ID!, $depth: Int, $include: [RelatedKind!]) {
    catalogRelated(kind: $kind, id: $id, depth: $depth, include: $include) {
      seed {
        kind
        id
        chainId
      }
      nodes {
        ref {
          kind
          id
          chainId
        }
        name
        relation
        via {
          kind
          id
          chainId
        }
        depth
      }
      edges {
        source {
          kind
          id
          chainId
        }
        target {
          kind
          id
          chainId
        }
        relation
      }
      counts {
        kind
        count
      }
      depth
      truncated
      partial
    }
  }
`
);
var toEnum = (kind) => kind.toUpperCase();
var fromEnum = (value) => value.toLowerCase();
function mapRef(ref) {
  return {
    kind: fromEnum(ref.kind),
    id: ref.id,
    ...ref.chainId ? { chainId: ref.chainId } : {}
  };
}
function mapRelatedGraph(data) {
  const g = data.catalogRelated;
  if (!g) return null;
  const counts = {};
  for (const { kind, count } of g.counts ?? []) {
    counts[fromEnum(kind)] = count;
  }
  return {
    seed: mapRef(g.seed),
    nodes: (g.nodes ?? []).map((n) => ({
      ref: mapRef(n.ref),
      ...n.name ? { name: n.name } : {},
      ...n.relation ? { relation: fromEnum(n.relation) } : {},
      ...n.via ? { via: mapRef(n.via) } : {},
      depth: n.depth
    })),
    edges: (g.edges ?? []).map((e) => ({
      source: mapRef(e.source),
      target: mapRef(e.target),
      relation: fromEnum(e.relation)
    })),
    counts,
    depth: g.depth,
    truncated: g.truncated,
    partial: g.partial
  };
}
async function fetchCatalogRelated(client, kind, id, args = {}, opts) {
  const data = await client.request(
    CATALOG_RELATED_QUERY,
    {
      kind: toEnum(kind),
      id,
      depth: args.depth,
      include: args.include?.map(toEnum)
    },
    opts
  );
  return mapRelatedGraph(data);
}
function relatedChildren(graph, ref) {
  const byRelation = /* @__PURE__ */ new Map();
  for (const edge of graph.edges) {
    if (edge.source.kind !== ref.kind || edge.source.id !== ref.id) continue;
    const list = byRelation.get(edge.relation) ?? [];
    list.push(edge.target);
    byRelation.set(edge.relation, list);
  }
  return [...byRelation.entries()].map(([relation, targets]) => ({ relation, targets }));
}
function relatedOfKind(graph, kind) {
  return graph.nodes.filter((n) => n.ref.kind === kind && n.depth > 0);
}

// src/tenancy/rungs.ts
var RUNG_ORDER = ["global", "customer", "org", "tenant", "user"];
var RUNG_ALIASES = {
  system: "global",
  platform: "global"
};
function isRung(value) {
  return typeof value === "string" && RUNG_ORDER.includes(value);
}
function canonicalRung(value) {
  if (typeof value !== "string") return null;
  const lower = value.trim().toLowerCase();
  if (isRung(lower)) return lower;
  return RUNG_ALIASES[lower] ?? null;
}

// src/graphql/operations/address.ts
var ADDRESS_SCOPES = ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"];
function toAddressScope(scope) {
  const rung = canonicalRung(scope);
  if (!rung) return void 0;
  const upper = rung.toUpperCase();
  return ADDRESS_SCOPES.includes(upper) ? upper : void 0;
}
var RESOLVED_ENTITY_FIELDS = (
  /* GraphQL */
  `
  id
  name
  type
  logo
  color
  confidence
  confidenceStatus
  verified
`
);
var RESOLVED_HIT_CORE_FIELDS = (
  /* GraphQL */
  `
  kind
  catalogId
  name
  color
  logo
  tierWeight
  isPrimary
  severity
  riskLevel
  confidence
  confidenceStatus
  lifecycle
  explanation
`
);
var RESOLVED_HIT_FIELDS = (
  /* GraphQL */
  `
  ${RESOLVED_HIT_CORE_FIELDS}
  availableScopes {
    scope
    tierWeight
    overrides {
      key
      value
    }
  }
`
);
function resolvedBundleFields(hitFields) {
  return (
    /* GraphQL */
    `
    address
    chainId
    asOf
    entities { ${RESOLVED_ENTITY_FIELDS} }
    tags { ${hitFields} }
    labels { ${hitFields} }
    groups { ${hitFields} }
    primary {
      kind
      catalogId
    }
  `
  );
}
var ADDRESS_OVERLAYS_QUERY = (
  /* GraphQL */
  `
  query AddressOverlays($address: String!, $chainId: String, $asOf: Int, $scope: AddressScope) {
    address(address: $address, chainId: $chainId, asOf: $asOf, scope: $scope) {
      ${resolvedBundleFields(RESOLVED_HIT_FIELDS)}
    }
  }
`
);
var clean2 = (v) => v == null ? void 0 : v;
function mapScope(scope) {
  const overrides = {};
  for (const { key, value } of scope.overrides ?? []) overrides[key] = value;
  return { scope: scope.scope, tierWeight: scope.tierWeight, overrides };
}
function mapHit(hit) {
  return {
    kind: hit.kind,
    catalogId: hit.catalogId,
    name: clean2(hit.name),
    color: clean2(hit.color),
    logo: clean2(hit.logo),
    tierWeight: hit.tierWeight,
    isPrimary: hit.isPrimary,
    severity: clean2(hit.severity),
    riskLevel: clean2(hit.riskLevel),
    confidence: clean2(hit.confidence),
    confidence_status: clean2(hit.confidenceStatus),
    lifecycle: clean2(hit.lifecycle),
    explanation: hit.explanation ?? [],
    availableScopes: (hit.availableScopes ?? []).map(mapScope)
  };
}
function mapEntity(entity) {
  return {
    id: entity.id,
    name: clean2(entity.name),
    type: clean2(entity.type),
    logo: clean2(entity.logo),
    color: clean2(entity.color),
    confidence: clean2(entity.confidence),
    confidence_status: clean2(entity.confidenceStatus),
    verified: entity.verified ?? void 0
  };
}
function mapBundle(a) {
  return {
    address: a.address,
    chainId: clean2(a.chainId),
    asOf: a.asOf,
    entities: (a.entities ?? []).map(mapEntity),
    tags: (a.tags ?? []).map(mapHit),
    labels: (a.labels ?? []).map(mapHit),
    groups: (a.groups ?? []).map(mapHit),
    primary: a.primary ?? []
  };
}
function mapAddressOverlays(data) {
  return data.address ? mapBundle(data.address) : null;
}
async function fetchAddressOverlays(client, args, opts) {
  const data = await client.request(
    ADDRESS_OVERLAYS_QUERY,
    { address: args.address, chainId: args.chainId, asOf: args.asOf, scope: args.scope },
    opts
  );
  return mapAddressOverlays(data);
}

// src/graphql/operations/addressDetail.ts
var ADDRESS_ROW_FIELDS = `
  address
  chain
  addressType
  isPrimaryChain
  name
  notes
  symbol
  decimals
  isContract
  deployer
  deployedAt
  lastSeenAt
  riskLevel
  severity
  lifecycle
  isHumanApproved
  enrichmentStatus
  primaryEntityId
  logoS3Uri
  aliases
  source
  confidence
  systemDefault
  metadata
`;
var ADDRESS_DETAIL_QUERY = (
  /* GraphQL */
  `
  query AddressDetail($address: String!, $chainId: String!, $asOf: Int, $scope: AddressScope, $withScopes: Boolean) {
    addressDetail(address: $address, chainId: $chainId, asOf: $asOf, scope: $scope, withScopes: $withScopes) {
      saved
      ownOverride
      row { ${ADDRESS_ROW_FIELDS} }
      otherScopes {
        scope
        readable
        count
        overrides { key value }
      }
    }
  }
`
);
function mapAddressRow(row) {
  return {
    address: row.address,
    chain: row.chain,
    address_type: row.addressType ?? void 0,
    is_primary_chain: Boolean(row.isPrimaryChain),
    name: row.name ?? null,
    notes: row.notes ?? null,
    symbol: row.symbol ?? null,
    decimals: row.decimals ?? null,
    is_contract: row.isContract ?? null,
    deployer: row.deployer ?? null,
    deployed_at: row.deployedAt ?? null,
    last_seen_at: row.lastSeenAt ?? null,
    risk_level: row.riskLevel ?? void 0,
    severity: row.severity ?? void 0,
    lifecycle: row.lifecycle ?? void 0,
    // The pair `isVerified`/`isBeingMapped` read. Projected here so the detail
    // panel judges a row the same way whichever mouth answered it.
    is_human_approved: Boolean(row.isHumanApproved),
    enrichment_status: row.enrichmentStatus ?? null,
    primary_entity_id: row.primaryEntityId ?? null,
    logo_s3_uri: row.logoS3Uri ?? null,
    aliases: row.aliases ?? [],
    source: row.source ?? null,
    confidence: row.confidence ?? void 0,
    system_default: Boolean(row.systemDefault),
    metadata: row.metadata ?? {}
  };
}
function mapAddressRowScope(scope) {
  const overrides = {};
  for (const { key, value } of scope.overrides ?? []) overrides[key] = value;
  return {
    scope: scope.scope,
    readable: Boolean(scope.readable),
    count: scope.count ?? 0,
    overrides
  };
}
function mapAddressDetail(data) {
  const d = data.addressDetail;
  if (!d) return null;
  return {
    address: mapAddressRow(d.row),
    saved: Boolean(d.saved),
    own_override: Boolean(d.ownOverride),
    // The catalog overlay bundle is fetched separately by the detail UI (the
    // `address` overlays op), so it is intentionally not selected here.
    catalog: void 0,
    other_scopes: (d.otherScopes ?? []).map(mapAddressRowScope)
  };
}
async function graphqlFetchAddressDetail(client, args, opts) {
  const data = await client.request(
    ADDRESS_DETAIL_QUERY,
    {
      address: args.address,
      chainId: args.chainId,
      asOf: args.asOf,
      scope: args.scope,
      withScopes: args.withScopes ?? false
    },
    opts
  );
  return mapAddressDetail(data);
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var logging = globalSingleton("logging/state", () => ({
  sinks: [],
  /** Minimum level that reaches the console (debug-flag lowers it to `debug`). */
  minConsoleLevel: "warn"
}));
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of logging.sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[logging.minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  XRPL: "xrpl",
  TRON: "tron",
  NEAR: "near",
  OTHER: "other"
};
var EVM_IDENTIFIER_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.EVM,
  ChainFamily.HYPERCORE
]);
function hasEvmIdentifiers(family) {
  return family != null && EVM_IDENTIFIER_FAMILIES.has(family);
}
function isChainFamily(value) {
  return typeof value === "string" && Object.values(ChainFamily).includes(value);
}

// src/chains/catalogue.generated.json
var catalogue_generated_default = {
  chains: [
    {
      aliases: [
        "arbitrum-one",
        "arb",
        "arbitrum-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:42161",
      chain_id_hex: "0xa4b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://arbiscan.io",
      family: "evm",
      finality_blocks: 32,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Arbitrum",
      native_token: "ETH",
      providers: {
        alchemy: "arb-mainnet",
        ankr: "arbitrum",
        arkham: "arbitrum_one",
        blockchair: "arbitrum",
        blockscan: "42161",
        blockscout: "https://arbitrum.blockscout.com/api",
        blocksec: "42161",
        codeslaw: "arbitrum",
        coingecko: "arbitrum-one",
        defillama: "arbitrum",
        dexscreener: "arbitrum",
        etherscan_scan: "arbiscan.io",
        infura: "arbitrum-mainnet",
        metasleuth: "arbitrum",
        moralis: "arbitrum",
        sourcify: "42161"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://arb1.arbitrum.io/rpc",
        "https://arbitrum-one-rpc.publicnode.com"
      ],
      slug: "arbitrum",
      status: "active",
      wrapped_native_address: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:421614",
      chain_id_hex: "0x66eee",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.arbiscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:42161",
      metadata: {},
      name: "Arbitrum Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "arbitrum_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161554",
      chain_id_hex: "0x4e454152",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.aurora.dev",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Aurora",
      native_token: "ETH",
      providers: {
        defillama: "aurora",
        dexscreener: "aurora"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "aurora",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161555",
      chain_id_hex: "0x4e454153",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.aurora.dev",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "sidechain",
      mainnet: "eip155:1313161554",
      metadata: {},
      name: "Aurora Testnet",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "aurora_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "avax",
        "avalanche-c-chain",
        "avax-c-chain",
        "avalanche-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#E84142",
      caip2: "eip155:43114",
      chain_id_hex: "0xa86a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://snowtrace.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Avalanche",
      native_token: "AVAX",
      providers: {
        alchemy: "avax-mainnet",
        ankr: "avalanche",
        arkham: "avalanche",
        blockchair: "avalanche",
        blockscan: "43114",
        blocksec: "43114",
        coingecko: "avalanche",
        defillama: "avax",
        dexscreener: "avalanche",
        etherscan_scan: "snowtrace.io",
        infura: "avalanche-mainnet",
        metasleuth: "avalanche",
        moralis: "avalanche",
        sourcify: "43114"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.avax.network/ext/bc/C/rpc",
        "https://avalanche-c-chain-rpc.publicnode.com"
      ],
      slug: "avalanche",
      status: "active",
      wrapped_native_address: "0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7"
    },
    {
      aliases: [
        "base-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:8453",
      chain_id_hex: "0x2105",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://basescan.org",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Base",
      native_token: "ETH",
      providers: {
        alchemy: "base-mainnet",
        ankr: "base",
        arkham: "base",
        blockchair: "base",
        blockscan: "8453",
        blockscout: "https://base.blockscout.com/api",
        codeslaw: "base",
        coingecko: "base",
        defillama: "base",
        dexscreener: "base",
        etherscan_scan: "basescan.org",
        infura: "base-mainnet",
        metasleuth: "base",
        moralis: "base",
        sourcify: "8453"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.base.org",
        "https://base-rpc.publicnode.com"
      ],
      slug: "base",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84531",
      chain_id_hex: "0x14a33",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "base_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84532",
      chain_id_hex: "0x14a34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.basescan.org",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Sepolia",
      native_token: "ETH",
      providers: {
        moralis: "base_sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "base_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-mainnet",
        "0x0",
        "btc"
      ],
      bech32_hrp: null,
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000019d6689c085ae165831e93",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: "https://blockstream.info",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Bitcoin",
      native_token: "BTC",
      providers: {
        blockchair: "bitcoin",
        dexscreener: "bitcoin",
        esplora: "mainnet",
        scanscope: "bitcoin"
      },
      reorg_depth: 6,
      rpc_urls: [],
      slug: "bitcoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000933ea01ad0ee984209779ba",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "utxo",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "bip122:000000000019d6689c085ae165831e93",
      metadata: {},
      name: "Bitcoin Testnet",
      native_token: "BTC",
      providers: {
        esplora: "testnet"
      },
      reorg_depth: 6,
      rpc_urls: [],
      slug: "bitcoin-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "blast-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:81457",
      chain_id_hex: "0x13e31",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blastscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Blast",
      native_token: "ETH",
      providers: {
        arkham: "blast",
        codeslaw: "blast",
        defillama: "blast",
        dexscreener: "blast",
        sourcify: "81457"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://blast-rpc.publicnode.com"
      ],
      slug: "blast",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:168587773",
      chain_id_hex: "0xa0c71fd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.blastscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:81457",
      metadata: {},
      name: "Blast Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "blast_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "binance",
        "binance-smart-chain",
        "bnb",
        "bnb-chain",
        "bnb-smart-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:56",
      chain_id_hex: "0x38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bscscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "BNB Chain",
      native_token: "BNB",
      providers: {
        alchemy: "bnb-mainnet",
        ankr: "bsc",
        arkham: "bsc",
        blockchair: "binance-smart-chain",
        blockscan: "56",
        blocksec: "56",
        codeslaw: "bnbchain",
        coingecko: "binance-smart-chain",
        defillama: "bsc",
        dexscreener: "bsc",
        etherscan_scan: "bscscan.com",
        infura: "bsc-mainnet",
        metasleuth: "bsc",
        moralis: "bsc",
        sourcify: "56"
      },
      reorg_depth: 15,
      rpc_urls: [
        "https://bsc-dataseed1.binance.org",
        "https://bsc-rpc.publicnode.com"
      ],
      slug: "bsc",
      status: "active",
      wrapped_native_address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"
    },
    {
      aliases: [
        "bnb-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:97",
      chain_id_hex: "0x61",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.bscscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:56",
      metadata: {},
      name: "BSC Testnet",
      native_token: "tBNB",
      providers: {
        blockchair: "bsc-testnet",
        infura: "bsc-testnet",
        moralis: "bsc_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "bsc_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:42220",
      chain_id_hex: "0xa4ec",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://celoscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Celo",
      native_token: "CELO",
      providers: {
        defillama: "celo",
        dexscreener: "celo"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://celo-rpc.publicnode.com"
      ],
      slug: "celo",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:44787",
      chain_id_hex: "0xaef3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:42220",
      metadata: {},
      name: "Celo Alfajores",
      native_token: "CELO",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "celo_alfajores",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88888",
      chain_id_hex: "0x15b38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Chiliz",
      native_token: "CHZ",
      providers: {
        moralis: "chiliz"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "chiliz",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88882",
      chain_id_hex: "0x15b32",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "eip155:88888",
      metadata: {},
      name: "Chiliz Testnet",
      native_token: "CHZ",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "chiliz_spicy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cosmoshub-mainnet",
        "cosmoshub-4",
        "gaia"
      ],
      bech32_hrp: "cosmos",
      block_time_seconds: 5.5,
      brand_color: "#2E3148",
      caip2: "cosmos:cosmoshub-4",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/cosmos",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Cosmos Hub",
      native_token: "ATOM",
      providers: {
        cosmos_directory: "https://rest.cosmos.directory/cosmoshub",
        cosmos_lcd: "https://cosmos-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "cosmoshub",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cro"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#002D74",
      caip2: "eip155:25",
      chain_id_hex: "0x19",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://cronoscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Cronos",
      native_token: "CRO",
      providers: {
        arkham: "cronos",
        defillama: "cronos",
        dexscreener: "cronos",
        moralis: "cronos"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "cronos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 13,
      brand_color: "#328332",
      caip2: "eip155:61",
      chain_id_hex: "0x3d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.com/etc/mainnet",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {
        note: "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk."
      },
      name: "Ethereum Classic",
      native_token: "ETC",
      providers: {
        blockscout: "https://etc.blockscout.com/api"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://etc.etcdesktop.com"
      ],
      slug: "etc",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ethereum",
        "ethereum-mainnet",
        "eth-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:1",
      chain_id_hex: "0x1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://etherscan.io",
      family: "evm",
      finality_blocks: 96,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Ethereum",
      native_token: "ETH",
      providers: {
        alchemy: "eth-mainnet",
        ankr: "eth",
        arkham: "ethereum",
        blockchair: "ethereum",
        blockscan: "1",
        blockscout: "https://eth.blockscout.com/api",
        blocksec: "1",
        codeslaw: "ethereum",
        coingecko: "ethereum",
        defillama: "ethereum",
        dexscreener: "ethereum",
        etherscan_scan: "etherscan.io",
        infura: "mainnet",
        metasleuth: "eth",
        moralis: "eth",
        scanscope: "eth",
        sourcify: "1"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-rpc.publicnode.com"
      ],
      slug: "eth",
      status: "active",
      wrapped_native_address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
    },
    {
      aliases: [
        "ftm",
        "fantom-opera"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:250",
      chain_id_hex: "0xfa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://ftmscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Fantom",
      native_token: "FTM",
      providers: {
        blockchair: "fantom",
        blockscan: "250",
        blocksec: "250",
        coingecko: "fantom",
        defillama: "fantom",
        dexscreener: "fantom",
        etherscan_scan: "ftmscan.com",
        metasleuth: "fantom",
        sourcify: "250"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpcapi.fantom.network"
      ],
      slug: "fantom",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:4002",
      chain_id_hex: "0xfa2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:250",
      metadata: {},
      name: "Fantom Testnet",
      native_token: "FTM",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fantom_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:747",
      chain_id_hex: "0x2eb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evm.flowscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Flow",
      native_token: "FLOW",
      providers: {
        moralis: "flow"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "flow",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:545",
      chain_id_hex: "0x221",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.evm.flow.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:747",
      metadata: {},
      name: "Flow Testnet",
      native_token: "FLOW",
      providers: {
        moralis: "flow_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "flow_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:122",
      chain_id_hex: "0x7a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Fuse",
      native_token: "FUSE",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fuse",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:123",
      chain_id_hex: "0x7b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.fusespark.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:122",
      metadata: {},
      name: "Fuse Sparknet",
      native_token: "SPARK",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fuse_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xdai",
        "gnosis-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:100",
      chain_id_hex: "0x64",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosisscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Gnosis",
      native_token: "xDAI",
      providers: {
        ankr: "gnosis",
        arkham: "gnosis",
        blockchair: "gnosis",
        blockscan: "100",
        defillama: "xdai",
        dexscreener: "gnosis",
        moralis: "gnosis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gnosischain.com"
      ],
      slug: "gnosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:10200",
      chain_id_hex: "0x27d8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosis-chiado.blockscout.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "sidechain",
      mainnet: "eip155:100",
      metadata: {},
      name: "Gnosis Chiado",
      native_token: "xDAI",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "gnosis_chiado",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:5",
      chain_id_hex: "0x5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://goerli.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:325",
      chain_id_hex: "0x145",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "GRVT",
      native_token: "GBT",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "grvt",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666600000",
      chain_id_hex: "0x63564c40",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Harmony",
      native_token: "ONE",
      providers: {
        defillama: "harmony",
        dexscreener: "harmony"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "harmony",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666700000",
      chain_id_hex: "0x6357d2e0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1666600000",
      metadata: {},
      name: "Harmony Testnet",
      native_token: "ONE",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "harmony_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:17000",
      chain_id_hex: "0x4268",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Holesky",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "holesky",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hypercore-mainnet",
        "hyperliquid",
        "hyperliquid-core",
        "hyperliquid-l1"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:mainnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "HyperCore",
      native_token: "HYPE",
      providers: {
        hyperliquid: "mainnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "hypercore",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyperliquid-testnet",
        "hypercore_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:testnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid-testnet.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "hyperliq:mainnet",
      metadata: {},
      name: "HyperCore Testnet",
      native_token: "HYPE",
      providers: {
        hyperliquid: "testnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "hypercore-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyper-evm",
        "hyperliquid-evm"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:999",
      chain_id_hex: "0x3e7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://hyperevmscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "HyperEVM",
      native_token: "HYPE",
      providers: {
        evm_rpc: "ingest",
        sourcify: "999"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid.xyz/evm"
      ],
      slug: "hyperevm",
      status: "active",
      wrapped_native_address: "0x5555555555555555555555555555555555555555"
    },
    {
      aliases: [
        "hyperevm-testnet",
        "hyperliquid-evm-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:998",
      chain_id_hex: "0x3e6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:999",
      metadata: {},
      name: "HyperEVM Testnet",
      native_token: "HYPE",
      providers: {
        evm_rpc: "ingest",
        sourcify: "998"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid-testnet.xyz/evm"
      ],
      slug: "hyperevm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:57073",
      chain_id_hex: "0xdef1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.inkonchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Ink",
      native_token: "ETH",
      providers: {
        blockscout_v2: "https://explorer.inkonchain.com/api/v2",
        sourcify: "57073"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ink",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2222",
      chain_id_hex: "0x8ae",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Kava",
      native_token: "KAVA",
      providers: {
        defillama: "kava"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "kava",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2221",
      chain_id_hex: "0x8ad",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:2222",
      metadata: {},
      name: "Kava Testnet",
      native_token: "TKAVA",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "kava_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:8217",
      chain_id_hex: "0x2019",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Klaytn",
      native_token: "KLAY",
      providers: {
        defillama: "klaytn"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "klaytn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:1001",
      chain_id_hex: "0x3e9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:8217",
      metadata: {},
      name: "Klaytn Baobab",
      native_token: "KLAY",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "klaytn_baobab",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "linea-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59144",
      chain_id_hex: "0xe708",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://lineascan.build",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Linea",
      native_token: "ETH",
      providers: {
        arkham: "linea",
        defillama: "linea",
        dexscreener: "linea",
        etherscan_scan: "lineascan.build",
        moralis: "linea",
        sourcify: "59144"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.linea.build"
      ],
      slug: "linea",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59141",
      chain_id_hex: "0xe705",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.lineascan.build",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "linea_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59140",
      chain_id_hex: "0xe704",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "linea_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1135",
      chain_id_hex: "0x46f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.lisk.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Lisk",
      native_token: "LSK",
      providers: {
        moralis: "lisk"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "lisk",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4202",
      chain_id_hex: "0x106a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1135",
      metadata: {},
      name: "Lisk Sepolia",
      native_token: "LSK",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "lisk_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5000",
      chain_id_hex: "0x1388",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mantle.xyz",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Mantle",
      native_token: "MNT",
      providers: {
        arkham: "mantle",
        defillama: "mantle",
        dexscreener: "mantle"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mantle.xyz"
      ],
      slug: "mantle",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mantle-testnet",
        "mantle_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5003",
      chain_id_hex: "0x138b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.mantlescan.xyz",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:5000",
      metadata: {},
      name: "Mantle Sepolia",
      native_token: "MNT",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "mantle_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:1088",
      chain_id_hex: "0x440",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Metis",
      native_token: "METIS",
      providers: {
        defillama: "metis"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "metis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:588",
      chain_id_hex: "0x24c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1088",
      metadata: {},
      name: "Metis Goerli",
      native_token: "METIS",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "metis_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:143",
      chain_id_hex: "0x8f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://monadexplorer.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Monad",
      native_token: "MON",
      providers: {
        moralis: "monad"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "monad",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "monero-mainnet",
        "xmr"
      ],
      bech32_hrp: null,
      block_time_seconds: 120,
      brand_color: "#FF6600",
      caip2: "monero:418015bb9ae982a1975da7d79277c270",
      chain_id_hex: null,
      decimals: 12,
      explorer_paths: {
        address: null,
        token: null
      },
      explorer_url: "https://xmrchain.net",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Monero",
      native_token: "XMR",
      providers: {
        blockchair: "monero",
        blockchair_api: "monero",
        xmrchain: "mainnet"
      },
      reorg_depth: 10,
      rpc_urls: [],
      slug: "monero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1287",
      chain_id_hex: "0x507",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonbase.moonscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1284",
      metadata: {},
      name: "Moonbase",
      native_token: "DEV",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonbase",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1284",
      chain_id_hex: "0x504",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Moonbeam",
      native_token: "GLMR",
      providers: {
        defillama: "moonbeam",
        dexscreener: "moonbeam",
        metasleuth: "moonbeam",
        moralis: "moonbeam"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonbeam",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#C0C0C0",
      caip2: "eip155:1285",
      chain_id_hex: "0x505",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Moonriver",
      native_token: "MOVR",
      providers: {
        defillama: "moonriver",
        dexscreener: "moonriver",
        moralis: "moonriver"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonriver",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80001",
      chain_id_hex: "0x13881",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Mumbai",
      native_token: "MATIC",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "mumbai",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:mainnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://nearblocks.io",
      family: "near",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "NEAR",
      native_token: "NEAR",
      providers: {
        near_rpc: "https://archival-rpc.mainnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.fastnear.com",
        "https://archival-rpc.mainnet.near.org"
      ],
      slug: "near",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:testnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://testnet.nearblocks.io",
      family: "near",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "near:mainnet",
      metadata: {},
      name: "NEAR Testnet",
      native_token: "NEAR",
      providers: {
        near_rpc: "https://archival-rpc.testnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.near.org"
      ],
      slug: "near-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41443",
      chain_id_hex: "0xa1e3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-evm.nesa.ai",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Nesa",
      native_token: "NES",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "nesa",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:204",
      chain_id_hex: "0xcc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://opbnbscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "opBNB",
      native_token: "BNB",
      providers: {
        defillama: "op_bnb"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "opbnb",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "op",
        "op-mainnet",
        "optimism-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:10",
      chain_id_hex: "0xa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://optimistic.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Optimism",
      native_token: "ETH",
      providers: {
        alchemy: "opt-mainnet",
        arkham: "optimism",
        blockchair: "optimism",
        blockscan: "10",
        blockscout: "https://explorer.optimism.io/api",
        blocksec: "10",
        codeslaw: "optimism",
        coingecko: "optimistic-ethereum",
        defillama: "optimism",
        dexscreener: "optimism",
        etherscan_scan: "optimistic.etherscan.io",
        infura: "optimism-mainnet",
        metasleuth: "optimism",
        moralis: "optimism",
        sourcify: "10"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.optimism.io",
        "https://optimism-rpc.publicnode.com"
      ],
      slug: "optimism",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:420",
      chain_id_hex: "0x1a4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "optimism_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:11155420",
      chain_id_hex: "0xaa37dc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-optimism.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "optimism_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "osmosis-mainnet",
        "osmosis-1"
      ],
      bech32_hrp: "osmo",
      block_time_seconds: 1.6,
      brand_color: "#750BBB",
      caip2: "cosmos:osmosis-1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/osmosis",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Osmosis",
      native_token: "OSMO",
      providers: {
        cosmos_directory: "https://rest.cosmos.directory/osmosis",
        cosmos_lcd: "https://osmosis-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "osmosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11297108109",
      chain_id_hex: "0x2a15c308d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Palm",
      native_token: "PALM",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "palm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3338",
      chain_id_hex: "0xd0a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://peaq.subscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "peaq",
      native_token: "PEAQ",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "peaq",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9745",
      chain_id_hex: "0x2611",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://plasmascan.to",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Plasma",
      native_token: "XPL",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "plasma",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polkadot-mainnet",
        "0x162",
        "dot"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#E6007A",
      caip2: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
      chain_id_hex: null,
      decimals: 10,
      explorer_paths: {},
      explorer_url: "https://polkadot.subscan.io",
      family: "other",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Polkadot",
      native_token: "DOT",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polkadot",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "matic",
        "matic-network",
        "polygon-mainnet",
        "polygon-pos"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:137",
      chain_id_hex: "0x89",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://polygonscan.com",
      family: "evm",
      finality_blocks: 512,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Polygon",
      native_token: "MATIC",
      providers: {
        alchemy: "polygon-mainnet",
        arkham: "polygon",
        blockchair: "polygon",
        blockscan: "137",
        blockscout: "https://polygon.blockscout.com/api",
        blocksec: "137",
        codeslaw: "polygon",
        coingecko: "polygon-pos",
        defillama: "polygon",
        dexscreener: "polygon",
        etherscan_scan: "polygonscan.com",
        infura: "polygon-mainnet",
        metasleuth: "polygon",
        moralis: "polygon",
        sourcify: "137"
      },
      reorg_depth: 256,
      rpc_urls: [
        "https://polygon-bor-rpc.publicnode.com"
      ],
      slug: "polygon",
      status: "active",
      wrapped_native_address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80002",
      chain_id_hex: "0x13882",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://amoy.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Amoy",
      native_token: "MATIC",
      providers: {
        moralis: "polygon_amoy"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_amoy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polygon-zkevm",
        "zkevm"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1101",
      chain_id_hex: "0x44d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://zkevm.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Polygon zkEVM",
      native_token: "ETH",
      providers: {
        arkham: "polygon_zkevm"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2442",
      chain_id_hex: "0x98a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1101",
      metadata: {},
      name: "Polygon zkEVM Cardona",
      native_token: "MATIC",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_zkevm_cardona",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "pls"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:369",
      chain_id_hex: "0x171",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.pulsechain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "PulseChain",
      native_token: "PLS",
      providers: {
        arkham: "pulsechain",
        moralis: "pulsechain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "pulsechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4663",
      chain_id_hex: "0x1237",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://robinhoodchain.blockscout.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Robinhood Chain",
      native_token: "ETH",
      providers: {
        blockscout_v2: "https://robinhoodchain.blockscout.com/api/v2",
        coingecko: "robinhood",
        defillama: "robinhood",
        evm_rpc: "discovery",
        moralis: "robinhood"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.chain.robinhood.com"
      ],
      slug: "robinhood",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ron"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2020",
      chain_id_hex: "0x7e4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Ronin",
      native_token: "RON",
      providers: {
        arkham: "ronin",
        defillama: "ronin",
        moralis: "ronin"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ronin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2021",
      chain_id_hex: "0x7e5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://saigon-app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:2020",
      metadata: {},
      name: "Ronin Saigon Testnet",
      native_token: "RON",
      providers: {
        moralis: "ronin_saigon"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ronin_saigon",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "scroll-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534352",
      chain_id_hex: "0x82750",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scrollscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Scroll",
      native_token: "ETH",
      providers: {
        codeslaw: "scroll",
        defillama: "scroll",
        dexscreener: "scroll",
        etherscan_scan: "scrollscan.com"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.scroll.io"
      ],
      slug: "scroll",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534351",
      chain_id_hex: "0x8274f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:534352",
      metadata: {},
      name: "Scroll Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "scroll_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sei-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1329",
      chain_id_hex: "0x531",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://seitrace.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Sei",
      native_token: "SEI",
      providers: {
        moralis: "sei"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "sei",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1328",
      chain_id_hex: "0x530",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1329",
      metadata: {},
      name: "Sei Testnet",
      native_token: "SEI",
      providers: {
        moralis: "sei_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "sei_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:11155111",
      chain_id_hex: "0xaa36a7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Sepolia",
      native_token: "ETH",
      providers: {
        alchemy: "eth-sepolia",
        ankr: "eth_sepolia",
        infura: "sepolia",
        moralis: "sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-sepolia-rpc.publicnode.com"
      ],
      slug: "sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "solana-mainnet",
        "0x1399c",
        "sol"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "account"
      },
      explorer_url: "https://solscan.io",
      family: "solana",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Solana",
      native_token: "SOL",
      providers: {
        arkham: "solana",
        blockchair: "solana",
        coingecko: "solana",
        defillama: "solana",
        dexscreener: "solana",
        helius: "mainnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://solana-rpc.publicnode.com",
        "https://api.mainnet-beta.solana.com"
      ],
      slug: "solana",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Devnet",
      native_token: "SOL",
      providers: {
        helius: "devnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.devnet.solana.com"
      ],
      slug: "solana-devnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Testnet",
      native_token: "SOL",
      providers: {
        helius: "testnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.testnet.solana.com"
      ],
      slug: "solana-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "thorchain-mainnet"
      ],
      bech32_hrp: "thor",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:thorchain-1",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        address: "address",
        block: "block",
        token: null,
        tx: "tx"
      },
      explorer_url: "https://thorchain.net",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "THORChain",
      native_token: "RUNE",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://gateway.liquify.com/chain/thorchain_api"
      ],
      slug: "thorchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "trx",
        "tron-mainnet",
        "tron:0x2b6653dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:728126428",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "TRON",
      native_token: "TRX",
      providers: {
        blockchair: "tron",
        tronscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.trongrid.io"
      ],
      slug: "tron",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-testnet-nile",
        "tron:0xcd8690dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:3448148188",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://nile.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Nile",
      native_token: "TRX",
      providers: {
        tronscan: "nile"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://nile.trongrid.io"
      ],
      slug: "tron-nile",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-testnet-shasta",
        "tron:0x94a9059e"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:2494104990",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://shasta.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Shasta",
      native_token: "TRX",
      providers: {
        tronscan: "shasta"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.shasta.trongrid.io"
      ],
      slug: "tron-shasta",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl",
        "ripple",
        "xrp-mainnet",
        "xrpl-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:0",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "XRP Ledger",
      native_token: "XRP",
      providers: {
        blockchair: "ripple",
        xrpscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s1.ripple.com:51234",
        "https://s2.ripple.com:51234"
      ],
      slug: "xrp",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://testnet.xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "xrpl:0",
      metadata: {},
      name: "XRP Ledger Testnet",
      native_token: "XRP",
      providers: {
        xrpscan: "testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s.altnet.rippletest.net:51234"
      ],
      slug: "xrp-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zcash-mainnet",
        "zec"
      ],
      bech32_hrp: null,
      block_time_seconds: 75,
      brand_color: "#F4B728",
      caip2: "bip122:00040fe8ec8471911baa1db1266ea15d",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/zcash",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Zcash",
      native_token: "ZEC",
      providers: {
        blockchair: "zcash",
        blockchair_api: "zcash"
      },
      reorg_depth: 24,
      rpc_urls: [],
      slug: "zcash",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7000",
      chain_id_hex: "0x1b58",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zetachain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "ZetaChain",
      native_token: "ZETA",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zetachain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7001",
      chain_id_hex: "0x1b59",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.zetascan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:7000",
      metadata: {},
      name: "Zetachain Testnet",
      native_token: "ZETA",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zetachain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zksync",
        "zksync-era",
        "era"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:324",
      chain_id_hex: "0x144",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "zkSync Era",
      native_token: "ETH",
      providers: {
        alchemy: "zksync-mainnet",
        arkham: "zksync_era",
        coingecko: "zksync",
        defillama: "era",
        dexscreener: "zksync",
        infura: "zksync-mainnet",
        sourcify: "324"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.era.zksync.io"
      ],
      slug: "zksync_era",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:300",
      chain_id_hex: "0x12c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zksync_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:280",
      chain_id_hex: "0x118",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zksync_testnet",
      status: "deprecated",
      wrapped_native_address: null
    }
  ],
  provider_keys: {
    alchemy: "Alchemy network subdomain: https://{id}.g.alchemy.com/v2/<key>",
    ankr: "Ankr RPC path: https://rpc.ankr.com/{id}/<key>",
    arkham: "Arkham chain name",
    blockchair: "Blockchair chain name",
    blockchair_api: "Blockchair API chain (UTXO ingest alongside xmrchain)",
    blockscan: "Blockscan chain id",
    blockscout: "Blockscout Etherscan-compatible API base (ABIs, source)",
    blockscout_v2: "Blockscout v2 API base that ingests the chain's transactions",
    blocksec: "BlockSec (MetaSleuth API) chain id",
    codeslaw: "Codeslaw platform name",
    coingecko: "CoinGecko asset platform id",
    cosmos_directory: "cosmos.directory LCD proxy base URL, the Cosmos fallback",
    cosmos_lcd: "Cosmos SDK LCD (REST) base URL, first choice (Cosmos ingest)",
    defillama: "DefiLlama chain name",
    dexscreener: "DexScreener chain id",
    esplora: "Esplora network (UTXO ingest)",
    etherscan_scan: "Etherscan-family explorer host scraped for labels",
    evm_rpc: "'ingest' (transactions read over the chain's JSON-RPC) or 'discovery' (block windows found over it, transactions read through another route)",
    helius: "Helius cluster (Solana ingest)",
    hyperliquid: "Hyperliquid network (HyperCore ingest)",
    infura: "Infura network subdomain: https://{id}.infura.io/v3/<key>",
    metasleuth: "MetaSleuth chain name",
    moralis: "Moralis chain name; the Moralis ingest route reads every chain carrying it",
    near_rpc: "NEAR archival RPC URL (NEAR ingest)",
    scanscope: "ScanScope chain name",
    sourcify: "Sourcify chain id",
    tronscan: "TronScan network (TRON ingest)",
    xmrchain: "xmrchain onion-explorer network (Monero ingest)",
    xrpscan: "XRPScan network (XRP Ledger ingest)"
  }
};
var ChainStatus = {
  /** Offered in pickers and processed. */
  ACTIVE: "active"};
var EXPLORER_LINK_TYPES = ["tx", "address", "block", "token"];
var ChainWireSchema = z.object({
  caip2: z.string().min(1),
  slug: z.string().min(1),
  chain_id_hex: z.string().nullable().default(null),
  family: z.string().refine(isChainFamily, { message: "unknown chain family" }),
  aliases: z.array(z.string()).default([]),
  name: z.string().min(1),
  native_token: z.string().min(1),
  decimals: z.number().int().min(0).default(18),
  wrapped_native_address: z.string().nullable().default(null),
  status: z.enum(["active", "deprecated", "disabled"]).default("active"),
  is_testnet: z.boolean().default(false),
  mainnet: z.string().nullable().default(null),
  layer: z.enum(["l1", "l2", "sidechain"]).nullable().default(null),
  // Required: rows arrive resolved (Python fills the mainnet's / family's depth).
  reorg_depth: z.number().int().min(0),
  block_time_seconds: z.number().positive().nullable().default(null),
  finality_blocks: z.number().int().positive().nullable().default(null),
  explorer_url: z.string().nullable().default(null),
  explorer_paths: z.record(z.string(), z.string().nullable()).default({}),
  rpc_urls: z.array(z.string()).default([]),
  providers: z.record(z.string(), z.string()).default({}),
  bech32_hrp: z.string().nullable().default(null),
  brand_color: z.string().nullable().default(null),
  metadata: z.record(z.string(), z.unknown()).default({})
});
function foldAliases(aliases) {
  const seen = /* @__PURE__ */ new Set();
  for (const raw of aliases ?? []) {
    const alias = raw.trim().toLowerCase();
    if (alias) seen.add(alias);
  }
  return [...seen];
}
function explorerPathsOf(raw) {
  const out = {};
  for (const type of EXPLORER_LINK_TYPES) if (type in raw) out[type] = raw[type] ?? null;
  return out;
}
function chainFromWire(row, mainnetSlugOf) {
  const wire = ChainWireSchema.parse(row);
  return Object.freeze({
    caip2: wire.caip2,
    slug: wire.slug,
    chainIdHex: wire.chain_id_hex,
    family: wire.family,
    aliases: Object.freeze(foldAliases(wire.aliases)),
    name: wire.name,
    nativeToken: wire.native_token,
    decimals: wire.decimals,
    wrappedNativeAddress: wire.wrapped_native_address,
    status: wire.status,
    isTestnet: wire.is_testnet,
    mainnet: wire.mainnet,
    mainnetSlug: wire.mainnet ? mainnetSlugOf?.(wire.mainnet) ?? null : null,
    layer: wire.layer,
    reorgDepth: wire.reorg_depth,
    blockTimeSec: wire.block_time_seconds,
    finalityBlocks: wire.finality_blocks,
    explorerUrl: wire.explorer_url ? wire.explorer_url.replace(/\/+$/, "") : null,
    explorerPaths: Object.freeze(explorerPathsOf(wire.explorer_paths)),
    rpcUrls: Object.freeze([...wire.rpc_urls]),
    providers: Object.freeze({ ...wire.providers }),
    bech32Hrp: wire.bech32_hrp,
    brandColor: wire.brand_color,
    logoUrl: null,
    metadata: Object.freeze({ ...wire.metadata })
  });
}
function isActiveChain(chain) {
  return chain.status === ChainStatus.ACTIVE;
}

// src/chains/catalogue.ts
var CatalogueSchema = z.object({
  chains: z.array(ChainWireSchema).default([]),
  provider_keys: z.record(z.string(), z.string()).default({})
});
var SNAPSHOT = CatalogueSchema.parse(catalogue_generated_default);
var BUNDLED_SLUG_BY_CAIP2 = new Map(
  SNAPSHOT.chains.map((row) => [row.caip2, row.slug])
);
var BUNDLED_CHAINS = Object.freeze(
  SNAPSHOT.chains.map((row) => chainFromWire(row, (caip2) => BUNDLED_SLUG_BY_CAIP2.get(caip2)))
);
Object.freeze({
  ...SNAPSHOT.provider_keys
});

// src/chains/registry.ts
var log = createLogger("chains:registry");
function hexKey(value) {
  let token = value.trim().toLowerCase();
  if (!token) return null;
  if (!token.startsWith("0x")) token = `0x${token}`;
  try {
    return `0x${BigInt(token).toString(16)}`;
  } catch {
    return null;
  }
}
var ChainRegistry = class {
  byCaip2 = /* @__PURE__ */ new Map();
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  // Alternate spellings; consulted by `resolve` only after the canonical
  // lookups, so an alias can never shadow a caip2 / hex / slug.
  byAlias = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  revision = 0;
  batchDepth = 0;
  pending = false;
  sorted = null;
  constructor(chains = []) {
    for (const chain of chains) this.register(chain);
    this.pending = false;
  }
  /**
   * Changes whenever the answer to any lookup may have changed — a register,
   * an unregister, a hydration. What `useSyncExternalStore` snapshots.
   */
  get version() {
    return this.revision;
  }
  /**
   * Add `chain`; `replace: true` overwrites the row with the same CAIP-2 id.
   *
   * Throws when the chain's slug or hex id already names a DIFFERENT chain —
   * two rows can never share a spelling, with or without `replace` (Python's
   * `ChainRegistry.register` refuses the same).
   */
  register(chain, { replace = false } = {}) {
    const caip2 = normalizeCaip2(chain.caip2);
    const existing = this.byCaip2.get(caip2);
    if (existing && existing !== chain && !replace) {
      throw new Error(`Chain ${caip2} is already registered; pass replace=true to overwrite.`);
    }
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    const slugOwner = this.bySlug.get(chain.slug);
    if (slugOwner && normalizeCaip2(slugOwner.caip2) !== caip2) {
      throw new Error(`Chain slug "${chain.slug}" of ${caip2} already names ${slugOwner.caip2}.`);
    }
    const hexOwner = hex ? this.byHex.get(hex) : void 0;
    if (hexOwner && normalizeCaip2(hexOwner.caip2) !== caip2) {
      throw new Error(`Chain hex ${hex} of ${caip2} already names ${hexOwner.caip2}.`);
    }
    if (existing) this.drop(existing);
    this.byCaip2.set(caip2, chain);
    this.bySlug.set(chain.slug, chain);
    if (hex) this.byHex.set(hex, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
    this.touch();
  }
  /** Remove and return the chain `value` resolves to (any spelling). */
  unregister(value) {
    const chain = this.resolve(value);
    if (!chain) return null;
    this.drop(chain);
    this.touch();
    return chain;
  }
  drop(chain) {
    this.byCaip2.delete(normalizeCaip2(chain.caip2));
    if (this.bySlug.get(chain.slug) === chain) this.bySlug.delete(chain.slug);
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    if (hex && this.byHex.get(hex) === chain) this.byHex.delete(hex);
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  /** Drop every registered chain (test-only). */
  clear() {
    this.byCaip2.clear();
    this.bySlug.clear();
    this.byHex.clear();
    this.byAlias.clear();
    this.touch();
  }
  /**
   * Run `fn` and notify subscribers ONCE at the end, however many rows it
   * changed — a hydration of a hundred rows is one re-render, not a hundred.
   */
  batch(fn) {
    this.batchDepth += 1;
    try {
      return fn();
    } finally {
      this.batchDepth -= 1;
      if (this.batchDepth === 0 && this.pending) {
        this.pending = false;
        this.emit();
      }
    }
  }
  /** Call `listener` after every change; returns the unsubscribe. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  touch() {
    this.revision += 1;
    this.sorted = null;
    if (this.batchDepth > 0) this.pending = true;
    else this.emit();
  }
  emit() {
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (err) {
        log.error("chain registry listener threw", { err });
      }
    }
  }
  /** The chain with this slug (case-folded), or null. */
  get(slug) {
    return this.bySlug.get((slug ?? "").trim().toLowerCase()) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes, lowercases, drops leading zeros). */
  getByHex(chainIdHex) {
    const key = hexKey(chainIdHex ?? "");
    return key ? this.byHex.get(key) ?? null : null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"); Base58 references are case-sensitive. */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2 ?? "")) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire. Every status
   * resolves.
   */
  resolve(value) {
    if (value == null) return null;
    const token = String(value).trim();
    if (!token) return null;
    if (token.includes(":")) {
      const hit = this.getByCaip2(token);
      if (hit) return hit;
    }
    const folded = token.toLowerCase();
    if (folded.startsWith("0x")) {
      const hit = this.getByHex(folded);
      if (hit) return hit;
    } else if (/^\d+$/.test(folded)) {
      const hit = this.byHex.get(`0x${BigInt(folded).toString(16)}`);
      if (hit) return hit;
    }
    return this.bySlug.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  /** Every registered chain, whatever its status, sorted by slug. A fresh array. */
  all() {
    this.sorted ??= Object.freeze(
      [...this.byCaip2.values()].sort((a, b) => a.slug.localeCompare(b.slug))
    );
    return [...this.sorted];
  }
  /** The chains offered for new work (status `active`), sorted by slug. */
  active() {
    return this.all().filter(isActiveChain);
  }
  byFamily(family) {
    return this.all().filter((c) => c.family === family);
  }
};
var registry = globalSingleton(
  "chains/registry.v2",
  () => new ChainRegistry(BUNDLED_CHAINS)
);
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return (value.includes(":") ? normalizeCaip2(value) : value.trim().toLowerCase()) || null;
  return chain.caip2;
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
var BITCOIN_ADDRESS_NETWORKS = {
  bitcoin: { hrp: "bc", versions: [0, 5] },
  "bitcoin-testnet": { hrp: "tb", versions: [111, 196] },
  "bitcoin-regtest": { hrp: "bcrt", versions: [111, 196] }
};
function decodeBase58(address, alphabet = BASE58) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== alphabet[0]) break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return null;
  const separator = lower.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower.slice(0, separator);
  const data = [...lower.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function validBitcoinAddress(address, network) {
  if (address.length < 14 || address.length > 90) return false;
  const formats = Object.values(BITCOIN_ADDRESS_NETWORKS);
  if (!formats.length) return false;
  if (/^(bc1|tb1|bcrt1)/i.test(address)) {
    const decoded = decodeBech32(address);
    if (!decoded || !formats.some((format) => format.hrp === decoded.hrp) || !decoded.data.length) return false;
    const version = decoded.data[0];
    if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
    const length = convertedLength(decoded.data.slice(1));
    return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && formats.some((format) => format.versions.includes(payload[0]));
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}
function validLedgerAddress(address, family) {
  const alphabet = family === "xrpl" ? "rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz" : BASE58;
  const bytes = decodeBase58(address, alphabet);
  if (!bytes || bytes.length !== 25 || bytes[0] !== (family === "xrpl" ? 0 : 65)) return false;
  const digest = sha256(sha256(bytes.slice(0, -4)));
  return bytes.slice(-4).every((value, index) => value === digest[index]);
}

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var RE_XRPL = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
var RE_TRON = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (chainId) {
    const fam = getChainMetadata(chainId)?.family;
    if (fam) return fam;
  }
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validBitcoinAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_XRPL.test(address) && validLedgerAddress(address, "xrpl")) return ChainFamily.XRPL;
  if (RE_TRON.test(address) && validLedgerAddress(address, "tron")) return ChainFamily.TRON;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr, chainId);
  if (hasEvmIdentifiers(fam)) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  if (fam === ChainFamily.COSMOS && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  if (fam === null && /^0x/i.test(addr)) return addr.toLowerCase();
  if (fam === ChainFamily.UTXO && RE_BTC_BECH32.test(addr) && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  return addr;
}

// src/overlays/model.ts
var clean3 = (v) => v == null || v === "" ? void 0 : v;
function mapWireScope(scope) {
  return {
    scope: scope.scope,
    tierWeight: scope.tier_weight ?? 0,
    overrides: scope.overrides ?? {}
  };
}
function mapWireHit(hit) {
  return {
    kind: hit.kind,
    catalogId: hit.catalog_id,
    name: clean3(hit.name),
    color: clean3(hit.color),
    logo: clean3(hit.logo),
    tierWeight: hit.tier_weight ?? 0,
    isPrimary: Boolean(hit.is_primary),
    severity: clean3(hit.severity),
    riskLevel: clean3(hit.risk_level),
    confidence: hit.confidence ?? void 0,
    lifecycle: clean3(hit.lifecycle),
    explanation: hit.explanation ?? [],
    availableScopes: (hit.available_scopes ?? []).map(mapWireScope)
  };
}
function mapWireEntity(entity) {
  return {
    id: entity.id,
    name: clean3(entity.name),
    type: clean3(entity.type),
    logo: clean3(entity.logo),
    color: clean3(entity.color),
    confidence: entity.confidence ?? void 0,
    verified: entity.verified ?? void 0
  };
}
function catalogBlockToResolvedOverlays(block, ctx) {
  const b = {};
  const primary = Object.entries(b.primary ?? {}).map(([kind, catalogId]) => ({
    kind,
    catalogId: String(catalogId)
  }));
  return {
    address: ctx.address,
    chainId: ctx.chainId,
    asOf: ctx.asOf ?? Math.floor(Date.now() / 1e3),
    entities: (b.entities ?? []).map(mapWireEntity),
    tags: (b.tags ?? []).map(mapWireHit),
    labels: (b.labels ?? []).map(mapWireHit),
    groups: (b.groups ?? []).map(mapWireHit),
    primary
  };
}

// src/overlays/batch.ts
function overlayRefKey(chainId, address) {
  return `${toCanonicalChainId(chainId) ?? chainId ?? ""}::${normalizeAddress(address, chainId)}`;
}
var EMPTY_BATCH_OVERLAYS = {
  byKey: /* @__PURE__ */ new Map(),
  byAddress: /* @__PURE__ */ new Map(),
  rowByKey: /* @__PURE__ */ new Map(),
  rowByAddress: /* @__PURE__ */ new Map(),
  savedKeys: /* @__PURE__ */ new Set()
};
function indexBatchEntries(entries) {
  const byKey = /* @__PURE__ */ new Map();
  const byAddress = /* @__PURE__ */ new Map();
  const rowByKey = /* @__PURE__ */ new Map();
  const rowByAddress = /* @__PURE__ */ new Map();
  const savedKeys = /* @__PURE__ */ new Set();
  for (const entry of entries) {
    if (!entry.address) continue;
    const addressKey = normalizeAddress(entry.address);
    const keys = /* @__PURE__ */ new Set([overlayRefKey(entry.chain, entry.address)]);
    if (entry.askedChain !== void 0) keys.add(overlayRefKey(entry.askedChain, entry.address));
    for (const key of keys) byKey.set(key, entry.overlays);
    byAddress.set(addressKey, entry.overlays);
    if (entry.row) {
      for (const key of keys) rowByKey.set(key, entry.row);
      rowByAddress.set(addressKey, entry.row);
      if (entry.saved) for (const key of keys) savedKeys.add(key);
    }
  }
  return { byKey, byAddress, rowByKey, rowByAddress, savedKeys };
}
function dedupeRefs(refs) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const ref of refs) {
    const address = (ref.address ?? "").trim();
    if (!address) continue;
    const key = overlayRefKey(ref.chainId, address);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ address, chainId: ref.chainId ?? null });
  }
  return out;
}

// src/graphql/operations/addressList.ts
var ADDRESSES_QUERY = (
  /* GraphQL */
  `
  query Addresses(
    $scope: AddressListScope
    $entityId: String
    $includeGlobal: Boolean
    $limit: Int
    $cursor: String
    $resolveScope: AddressScope
    $addressType: String
    $chainIds: [String!]
  ) {
    addresses(
      scope: $scope
      entityId: $entityId
      includeGlobal: $includeGlobal
      limit: $limit
      cursor: $cursor
      resolveScope: $resolveScope
      addressType: $addressType
      chainIds: $chainIds
    ) {
      items {
        row { ${ADDRESS_ROW_FIELDS} }
        catalog { ${resolvedBundleFields(RESOLVED_HIT_CORE_FIELDS)} }
      }
      count
      nextCursor
    }
  }
`
);
var LIST_MODE_ENUM = {
  tenant: "TENANT",
  entity: "ENTITY"
};
function graphqlListMode(mode) {
  return LIST_MODE_ENUM[mode ?? "tenant"] ?? null;
}
var ARGUMENT_DISPOSITION = {
  /** Translated to the SDL's enum; `cross_chain` has no member. */
  mode: "translated",
  entity_id: "sent",
  include_global: "sent",
  limit: "sent",
  cursor: "sent",
  address_type: "sent",
  chain_ids: "sent",
  /**
   * NO `address` argument exists on `addresses`. The single-address lookup is
   * server-side, answers completely and returns no cursor; a page of everything
   * is not a narrower version of that answer, it is the wrong one.
   */
  address: "unsupported"
};
var UNSUPPORTED_REASON = {
  address: "single-address search"
};
function stated(value) {
  if (value === void 0 || value === null || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value !== "";
  return true;
}
function addressListUnroutable(query = {}) {
  if (graphqlListMode(query.mode) === null) return `mode "${query.mode}"`;
  for (const [field, disposition] of Object.entries(ARGUMENT_DISPOSITION)) {
    if (disposition !== "unsupported") continue;
    const key = field;
    if (stated(query[key])) return UNSUPPORTED_REASON[key] ?? field;
  }
  return null;
}
function mapAddressPage(data) {
  const page = data.addresses;
  const addresses = (page?.items ?? []).map((item) => mapAddressRow(item.row));
  return {
    addresses,
    next_cursor: page?.nextCursor ?? null,
    // The SDL types `count` non-null, so the fallback is belt-and-braces — but a
    // 0 reported next to visible rows would make the table contradict its own
    // header, which is worth one `??`.
    count: page?.count ?? addresses.length
  };
}
function mapAddressListOverlays(data) {
  const entries = [];
  for (const item of data.addresses?.items ?? []) {
    if (!item.catalog) continue;
    const row = mapAddressRow(item.row);
    const address = item.catalog.address || row.address;
    if (!address) continue;
    entries.push({
      address,
      chain: item.catalog.chainId ?? row.chain,
      overlays: mapBundle(item.catalog),
      row,
      saved: true
    });
  }
  return indexBatchEntries(entries);
}
async function graphqlListAddresses(client, query = {}, opts) {
  return (await graphqlListAddressesWithOverlays(client, query, opts)).page;
}
async function graphqlListAddressesWithOverlays(client, query = {}, opts, resolveScope) {
  const unroutable = addressListUnroutable(query);
  if (unroutable) throw new Error(`address list: ${unroutable} has no GraphQL equivalent`);
  const scope = graphqlListMode(query.mode);
  const data = await client.request(
    ADDRESSES_QUERY,
    {
      scope,
      entityId: query.entity_id,
      includeGlobal: query.include_global,
      limit: query.limit,
      cursor: query.cursor,
      addressType: query.address_type || void 0,
      // Absent, not `[]`: an empty list reaches the server as a filter no chain
      // satisfies, which would answer "no addresses" for a cleared facet.
      chainIds: query.chain_ids?.length ? [...query.chain_ids] : void 0,
      // `USER` is the caller's own view, which is what omitting it already means.
      resolveScope: resolveScope && resolveScope !== "USER" ? resolveScope : void 0
    },
    opts
  );
  return { page: mapAddressPage(data), overlays: mapAddressListOverlays(data) };
}

// src/graphql/operations/addressCrossChain.ts
var ADDRESS_CROSS_CHAIN_QUERY = (
  /* GraphQL */
  `
  query AddressCrossChain($address: String!, $limit: Int, $resolveScope: AddressScope) {
    addressCrossChain(address: $address, limit: $limit, resolveScope: $resolveScope) {
      address
      count
      activity {
        address
        activeChainIds
        totalTxCount
        totalDefiCount
        totalNftCount
        totalTokenTransferCount
        firstSeenAt
        firstTxHash
        firstSeenChainId
        lastSeenAt
        lastTxHash
        lastSeenChainId
        busiestChainId
        refreshedAt
        chains
      }
      addresses {
        row { ${ADDRESS_ROW_FIELDS} }
        catalog { ${resolvedBundleFields(RESOLVED_HIT_CORE_FIELDS)} }
      }
    }
  }
`
);
function readJsonObject(value) {
  const parsed = typeof value === "string" ? safeParse(value) : value;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  return parsed;
}
function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
function mapCrossChainActivity(activity) {
  if (!activity) return null;
  return {
    address: activity.address,
    active_chain_ids: activity.activeChainIds ?? [],
    chains: readJsonObject(activity.chains),
    total_tx_count: activity.totalTxCount ?? 0,
    total_defi_count: activity.totalDefiCount ?? 0,
    total_nft_count: activity.totalNftCount ?? 0,
    total_token_transfer_count: activity.totalTokenTransferCount ?? 0,
    first_seen_at: activity.firstSeenAt,
    first_tx_hash: activity.firstTxHash,
    first_seen_chain_id: activity.firstSeenChainId,
    last_seen_at: activity.lastSeenAt,
    last_tx_hash: activity.lastTxHash,
    last_seen_chain_id: activity.lastSeenChainId,
    busiest_chain_id: activity.busiestChainId ?? null,
    refreshed_at: activity.refreshedAt
  };
}
function mapAddressCrossChain(data) {
  const lens = data.addressCrossChain;
  const addresses = (lens?.addresses ?? []).map((item) => {
    const row = mapAddressRow(item.row);
    return {
      ...row,
      // The two the SDL has no field for — same defaults the REST adapter
      // applies to a row that arrives without them. See the module note.
      saved: true,
      annotated: false,
      // `enrichment_status` IS on `AddressRow`, so `isBeingMapped` answers the
      // same on both transports. `mapAddressRow` already carries it; named here
      // because it is the field the whole "is this row still being worked on"
      // affordance hangs off.
      enrichment_status: row.enrichment_status ?? null,
      catalog: item.catalog ?? void 0
    };
  });
  return {
    addresses,
    activity: mapCrossChainActivity(lens?.activity ?? null),
    // The server's own count, not the page's: the two differ when `limit` clips,
    // and reporting the clipped number as the total is how "this address is on
    // 3 chains" gets said about one that is on forty.
    count: lens?.count ?? addresses.length
  };
}
function addressCrossChainIncomplete(lens) {
  if (lens.activity == null) {
    return "no stored activity rollup; only the REST lens consults the activity providers";
  }
  return null;
}
async function graphqlFetchAddressCrossChain(client, address, args = {}, opts) {
  const data = await client.request(
    ADDRESS_CROSS_CHAIN_QUERY,
    {
      address,
      limit: args.limit,
      // `USER` is the caller's own view, which is what omitting it already means.
      resolveScope: args.resolveScope && args.resolveScope !== "USER" ? args.resolveScope : void 0
    },
    opts
  );
  return mapAddressCrossChain(data);
}

// src/graphql/operations/addressBatch.ts
var GRAPHQL_BATCH_REF_LIMIT = 50;
function addressBatchRequest(refs, opts = {}) {
  const bundle = resolvedBundleFields(opts.withScopes ? RESOLVED_HIT_FIELDS : RESOLVED_HIT_CORE_FIELDS);
  const variables = {
    scope: opts.scope && opts.scope !== "USER" ? opts.scope : void 0
  };
  const declarations = ["$scope: AddressScope"];
  const fields = [];
  const withRow = [];
  refs.forEach((ref, i) => {
    const chain = ref.chainId || void 0;
    const row = Boolean(opts.includeRow && chain);
    variables[`a${i}`] = ref.address;
    variables[`c${i}`] = chain;
    withRow.push(row);
    if (row) {
      declarations.push(`$a${i}: String!`, `$c${i}: String!`);
      fields.push(
        `r${i}: addressDetail(address: $a${i}, chainId: $c${i}, scope: $scope, withScopes: $withScopes) {
          saved
          row { ${ADDRESS_ROW_FIELDS} }
          catalog { ${bundle} }
        }`
      );
    } else {
      declarations.push(`$a${i}: String!`, `$c${i}: String`);
      fields.push(`r${i}: address(address: $a${i}, chainId: $c${i}, scope: $scope) { ${bundle} }`);
    }
  });
  if (withRow.some(Boolean)) {
    declarations.push("$withScopes: Boolean");
    variables.withScopes = Boolean(opts.withScopes);
  }
  return {
    query: `query AddressBatch(${declarations.join(", ")}) {
${fields.join("\n")}
}`,
    variables,
    withRow
  };
}
async function graphqlResolveAddressesBatch(client, refs, opts = {}, requestOpts) {
  const cleaned = dedupeRefs(refs);
  if (cleaned.length === 0) return EMPTY_BATCH_OVERLAYS;
  if (cleaned.length > GRAPHQL_BATCH_REF_LIMIT) {
    throw new RangeError(
      `graphqlResolveAddressesBatch: ${cleaned.length} refs exceeds ${GRAPHQL_BATCH_REF_LIMIT}; use the REST batch`
    );
  }
  const { query, variables, withRow } = addressBatchRequest(cleaned, opts);
  const data = await client.request(query, variables, requestOpts);
  const entries = cleaned.map((ref, i) => {
    const empty = () => catalogBlockToResolvedOverlays(void 0, { address: ref.address, chainId: ref.chainId ?? void 0 });
    if (withRow[i]) {
      const detail = data[`r${i}`] ?? null;
      return {
        address: ref.address,
        askedChain: ref.chainId,
        chain: detail?.catalog?.chainId ?? detail?.row?.chain ?? ref.chainId,
        overlays: detail?.catalog ? mapBundle(detail.catalog) : empty(),
        row: detail?.row ? mapAddressRow(detail.row) : null,
        saved: Boolean(detail?.saved)
      };
    }
    const bundle = data[`r${i}`] ?? null;
    return {
      address: ref.address,
      askedChain: ref.chainId,
      chain: bundle?.chainId ?? ref.chainId,
      overlays: bundle ? mapBundle(bundle) : empty()
    };
  });
  return indexBatchEntries(entries);
}

// src/graphql/operations/transactions.ts
var LATEST_TRANSACTIONS_QUERY = (
  /* GraphQL */
  `
  query LatestTransactions($limit: Int, $cursor: String) {
    latestTransactions(limit: $limit, cursor: $cursor) {
      items {
        canonicalTxId
        chainId
        blockTimestamp
        entities {
          id
          name
        }
        tags {
          catalogId
          name
          color
          logo
        }
        labels {
          catalogId
          name
          color
          logo
        }
        groups {
          catalogId
          name
          color
          logo
        }
      }
      nextCursor
    }
  }
`
);
var clean4 = (v) => v == null ? void 0 : v;
function mapRef2(ref) {
  return {
    catalogId: ref.catalogId,
    name: clean4(ref.name),
    color: clean4(ref.color),
    logo: clean4(ref.logo)
  };
}
function mapItem(item) {
  return {
    canonicalTxId: item.canonicalTxId,
    chainId: item.chainId,
    blockTimestamp: item.blockTimestamp,
    entities: (item.entities ?? []).map((e) => ({ id: e.id, name: clean4(e.name) })),
    tags: (item.tags ?? []).map(mapRef2),
    labels: (item.labels ?? []).map(mapRef2),
    groups: (item.groups ?? []).map(mapRef2)
  };
}
function mapLatestTransactions(data) {
  const feed = data.latestTransactions ?? { items: [], nextCursor: null };
  return {
    items: (feed.items ?? []).map(mapItem),
    nextCursor: clean4(feed.nextCursor)
  };
}
async function fetchLatestTransactions(client, args = {}, opts) {
  const data = await client.request(
    LATEST_TRANSACTIONS_QUERY,
    { limit: args.limit, cursor: args.cursor },
    opts
  );
  return mapLatestTransactions(data);
}

// src/graphql/operations/transaction.ts
var TRANSACTION_HEADER_QUERY = (
  /* GraphQL */
  `
  query TransactionHeader($canonicalTxId: ID, $chainId: String, $txHash: String) {
    transaction(canonicalTxId: $canonicalTxId, chainId: $chainId, txHash: $txHash) {
      canonicalTxId
      chainId
      txHash
      blockNumber
      blockHash
      blockTimestamp
      finality
      saved
      hasBody
      enrichmentStatus
      runId
      notes
    }
  }
`
);
var clean5 = (v) => v == null ? void 0 : v;
function mapTransactionHeader(data) {
  const t = data.transaction;
  if (!t) return null;
  return {
    canonicalTxId: t.canonicalTxId,
    chainId: t.chainId,
    txHash: clean5(t.txHash),
    blockNumber: clean5(t.blockNumber),
    blockHash: clean5(t.blockHash),
    blockTimestamp: clean5(t.blockTimestamp),
    finality: clean5(t.finality),
    saved: Boolean(t.saved),
    hasBody: Boolean(t.hasBody),
    enrichmentStatus: clean5(t.enrichmentStatus),
    runId: clean5(t.runId),
    notes: clean5(t.notes)
  };
}
async function fetchTransactionHeader(client, args, opts) {
  const data = await client.request(
    TRANSACTION_HEADER_QUERY,
    { canonicalTxId: args.canonicalTxId, chainId: args.chainId, txHash: args.txHash },
    opts
  );
  return mapTransactionHeader(data);
}
var REQUEST_TRANSACTION_MUTATION = (
  /* GraphQL */
  `
  mutation RequestTransaction($chainId: String!, $txHash: String!, $decode: Boolean) {
    requestTransaction(chainId: $chainId, txHash: $txHash, decode: $decode) {
      canonicalTxId
      chainId
      txHash
      blockNumber
      blockHash
      blockTimestamp
      finality
      saved
      hasBody
      enrichmentStatus
      runId
      notes
    }
  }
`
);
async function graphqlRequestTransaction(client, args, opts) {
  const data = await client.request(
    REQUEST_TRANSACTION_MUTATION,
    { chainId: args.chainId, txHash: args.txHash, decode: args.decode ?? false },
    opts
  );
  return mapTransactionHeader({ transaction: data.requestTransaction });
}

// src/graphql/operations/assignments.ts
var CATALOG_KIND_ENUM = {
  entity: "ENTITY",
  tag: "TAG",
  label: "LABEL",
  group: "GROUP"
};
function toCatalogKindEnum(kind) {
  const enumValue = CATALOG_KIND_ENUM[kind?.toLowerCase?.() ?? ""];
  if (!enumValue) throw new Error(`catalog kind "${kind}" is not a GraphQL CatalogKind`);
  return enumValue;
}
var clean6 = (v) => v == null ? void 0 : v;
function toMemberTier(raw) {
  const value = raw?.toLowerCase?.();
  return value === "core" || value === "derived" ? value : null;
}
function toTierEnum(tier) {
  return tier == null ? void 0 : tier.toUpperCase();
}
function mapAssignment(row) {
  return {
    id: row.id,
    scope: row.scope,
    scope_target_id: row.scopeTargetId,
    chain_id: row.chainId ?? null,
    catalog_kind: row.catalogKind,
    catalog_id: row.catalogId,
    user_id: row.userId ?? null,
    is_human_approved: row.isHumanApproved,
    created_at: clean6(row.createdAt),
    confidence: clean6(row.confidence),
    confidence_status: clean6(row.confidenceStatus),
    explanation: row.explanation ?? [],
    // The ABAC carriers: who owns the overlay and who it is shared with. Left
    // out of the projection, a shared overlay came back looking private — and
    // the surfaces that offer sharing seed from exactly these rows.
    owner_user_id: row.ownerUserId ?? null,
    visible_to_user_ids: row.visibleToUserIds ?? [],
    visible_to_team_ids: row.visibleToTeamIds ?? []
    // Remaining CatalogAssignmentDTO fields (shadow keys) aren't part of the
    // lean overlay view and are left to the DTO's own defaults.
  };
}
function mapPrimaryPointer(row) {
  return {
    scope: "entity",
    scope_target_id: row.entityId,
    catalog_kind: "address",
    catalog_id: row.catalogId,
    chain_id: row.chainId
  };
}
function mapEntityMapping(row) {
  if (!row) return null;
  return {
    chain: row.chainId,
    address: row.address,
    entity_id: row.entityId,
    role: row.role ?? null,
    // `CORE` -> `core`: the schema declares its enums UPPER_SNAKE, the storage
    // vocabulary is lower-case, and the matrix compares against the latter.
    tier: toMemberTier(row.tier),
    confidence: row.confidence ?? void 0,
    confidence_status: row.confidenceStatus ?? void 0,
    valid_from: row.validFrom ?? null,
    valid_to: row.validTo ?? null,
    // Preserved as `undefined` when absent rather than coerced to a boolean:
    // "the read did not judge the window" is not "superseded", and a view that
    // conflated them would draw every current member as retired.
    active: row.active ?? void 0,
    is_human_approved: row.isHumanApproved,
    source: row.source ?? null,
    explanation: row.explanation ?? []
  };
}
var ASSIGNMENT_FIELDS = "id scope scopeTargetId chainId catalogKind catalogId userId isHumanApproved createdAt confidence confidenceStatus explanation ownerUserId visibleToUserIds visibleToTeamIds";
var ADDRESS_ASSIGNMENTS_QUERY = (
  /* GraphQL */
  `
  query AddressAssignments($address: String!, $chainId: String!, $limit: Int, $cursor: String) {
    addressAssignments(address: $address, chainId: $chainId, limit: $limit, cursor: $cursor) {
      items { ${ASSIGNMENT_FIELDS} }
      count
      nextCursor
    }
  }
`
);
var ADDRESS_ENTITY_MAPPING_QUERY = (
  /* GraphQL */
  `
  query AddressEntityMappingQuery($address: String!, $chainId: String!, $asOf: Int) {
    addressEntityMapping(address: $address, chainId: $chainId, asOf: $asOf) {
      entityId chainId address role tier confidence confidenceStatus validFrom validTo active isHumanApproved source explanation
    }
  }
`
);
var ASSIGN_ADDRESS_CATALOG_MUTATION = (
  /* GraphQL */
  `
  mutation AssignAddressCatalog($input: AssignCatalogInput!) {
    assignAddressCatalog(input: $input) { ${ASSIGNMENT_FIELDS} }
  }
`
);
var UNASSIGN_ADDRESS_CATALOG_MUTATION = (
  /* GraphQL */
  `
  mutation UnassignAddressCatalog($input: UnassignCatalogInput!) {
    unassignAddressCatalog(input: $input) { ok changed }
  }
`
);
var SET_ADDRESS_PRIMARY_MUTATION = (
  /* GraphQL */
  `
  mutation SetAddressPrimary($input: SetPrimaryInput!) {
    setAddressPrimary(input: $input) { entityId chainId catalogId }
  }
`
);
var CLEAR_ADDRESS_PRIMARY_MUTATION = (
  /* GraphQL */
  `
  mutation ClearAddressPrimary($input: ClearPrimaryInput!) {
    clearAddressPrimary(input: $input) { ok changed }
  }
`
);
var ENTITY_MAPPING_FIELDS = "entityId chainId address role tier confidence confidenceStatus validFrom validTo active isHumanApproved source explanation";
var PUT_ADDRESS_ENTITY_MAPPING_MUTATION = (
  /* GraphQL */
  `
  mutation PutAddressEntityMapping($input: PutEntityMappingInput!) {
    putAddressEntityMapping(input: $input) { ${ENTITY_MAPPING_FIELDS} }
  }
`
);
var DELETE_ADDRESS_ENTITY_MAPPING_MUTATION = (
  /* GraphQL */
  `
  mutation DeleteAddressEntityMapping($input: DeleteEntityMappingInput!) {
    deleteAddressEntityMapping(input: $input) { ok changed }
  }
`
);
var ENTITY_MEMBERSHIPS_QUERY = (
  /* GraphQL */
  `
  query EntityMemberships(
    $entityId: ID!
    $limit: Int
    $cursor: String
    $preferCore: Boolean
    $addressType: String
    $chainIds: [String!]
  ) {
    entityMemberships(
      entityId: $entityId
      limit: $limit
      cursor: $cursor
      preferCore: $preferCore
      addressType: $addressType
      chainIds: $chainIds
    ) {
      items { ${ENTITY_MAPPING_FIELDS} }
      count
      nextCursor
      primaryByChain
      memberTotal
      asOf
      lane
      truncated
    }
  }
`
);
var PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION = (
  /* GraphQL */
  `
  mutation PutAddressEntityMemberships($input: PutEntityMembershipsInput!) {
    putAddressEntityMemberships(input: $input) {
      count
      failed
      results {
        chainId
        address
        ok
        error
        mapping { ${ENTITY_MAPPING_FIELDS} }
      }
    }
  }
`
);
var ASSIGNMENTS_PAGE_BUDGET = 20;
async function graphqlListAssignments(client, args, opts) {
  const onePage = args.limit !== void 0 || args.cursor !== void 0;
  const out = [];
  let cursor = args.cursor;
  for (let page = 0; page < ASSIGNMENTS_PAGE_BUDGET; page += 1) {
    const data = await client.request(
      ADDRESS_ASSIGNMENTS_QUERY,
      { address: args.address, chainId: args.chainId, limit: args.limit, cursor },
      opts
    );
    const result = data.addressAssignments;
    for (const item of result?.items ?? []) out.push(mapAssignment(item));
    cursor = result?.nextCursor ?? void 0;
    if (onePage || !cursor) return out;
  }
  throw new GraphQLRequestError(
    `addressAssignments: more than ${ASSIGNMENTS_PAGE_BUDGET} pages for ${args.chainId}:${args.address}`
  );
}
function entityMembershipsUnroutable(query) {
  if (query.chains?.length) return "headline chain coverage (`chains`)";
  return null;
}
async function graphqlAttachAssignment(client, args, opts) {
  const { input } = args;
  const data = await client.request(
    ASSIGN_ADDRESS_CATALOG_MUTATION,
    {
      input: omitUndefined({
        address: args.address,
        chainId: args.chainId,
        kind: toCatalogKindEnum(input.catalog_kind),
        catalogId: input.catalog_id,
        userScoped: input.user_scoped ?? true,
        // `CatalogRung` — the SDL's enum spelling of a shared rung.
        rung: input.rung ? input.rung.toUpperCase() : void 0,
        visibleToUserIds: input.visible_to_user_ids,
        visibleToTeamIds: input.visible_to_team_ids,
        confidence: input.confidence,
        reason: input.reason,
        evidenceUrl: input.evidence_url,
        validFrom: input.valid_from,
        validTo: input.valid_to
      })
    },
    opts
  );
  return mapAssignment(data.assignAddressCatalog);
}
function omitUndefined(input) {
  const out = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== void 0) out[key] = value;
  }
  return out;
}
async function graphqlDetachAssignment(client, args, opts) {
  await client.request(
    UNASSIGN_ADDRESS_CATALOG_MUTATION,
    {
      input: omitUndefined({
        address: args.address,
        chainId: args.chainId,
        kind: toCatalogKindEnum(args.input.catalog_kind),
        catalogId: args.input.catalog_id,
        userScoped: args.input.user_scoped,
        rung: args.input.rung ? args.input.rung.toUpperCase() : void 0
      })
    },
    opts
  );
}
async function graphqlSetPrimary(client, args, opts) {
  const data = await client.request(
    SET_ADDRESS_PRIMARY_MUTATION,
    { input: { entityId: args.entityId, address: args.address, chainId: args.chainId } },
    opts
  );
  return mapPrimaryPointer(data.setAddressPrimary);
}
async function graphqlClearPrimary(client, args, opts) {
  await client.request(
    CLEAR_ADDRESS_PRIMARY_MUTATION,
    { input: { entityId: args.entityId, chainId: args.chainId } },
    opts
  );
}
async function graphqlGetEntityMapping(client, args, opts) {
  const data = await client.request(
    ADDRESS_ENTITY_MAPPING_QUERY,
    { address: args.address, chainId: args.chainId, asOf: args.asOf },
    opts
  );
  return mapEntityMapping(data.addressEntityMapping);
}
async function graphqlPutEntityMapping(client, args, opts) {
  const { input } = args;
  const data = await client.request(
    PUT_ADDRESS_ENTITY_MAPPING_MUTATION,
    {
      input: {
        address: args.address,
        chainId: args.chainId,
        entityId: input.entity_id,
        role: input.role,
        tier: toTierEnum(input.tier),
        confidence: input.confidence,
        validFrom: input.valid_from,
        validTo: input.valid_to
      }
    },
    opts
  );
  return mapEntityMapping(data.putAddressEntityMapping);
}
async function graphqlDeleteEntityMapping(client, args, opts) {
  const data = await client.request(
    DELETE_ADDRESS_ENTITY_MAPPING_MUTATION,
    { input: { address: args.address, chainId: args.chainId, entityId: args.entityId } },
    opts
  );
  return data.deleteAddressEntityMapping?.changed ?? 0;
}
async function graphqlListEntityMemberships(client, args, opts) {
  const data = await client.request(
    ENTITY_MEMBERSHIPS_QUERY,
    {
      entityId: args.entity_id,
      limit: args.limit,
      cursor: args.cursor,
      preferCore: args.prefer_core,
      addressType: args.address_type,
      // `chains` is NOT sent here: on this query it scopes the headline pointer,
      // and GraphQL derives that coverage itself. This one narrows the rows.
      chainIds: args.chain_ids?.length ? [...args.chain_ids] : void 0
    },
    opts
  );
  const page = data.entityMemberships;
  const mappings = (page?.items ?? []).map(mapEntityMapping).filter((m) => m != null);
  return {
    mappings,
    next_cursor: page?.nextCursor ?? null,
    count: page?.count ?? mappings.length,
    primary_by_chain: mapPrimaryByChain(page?.primaryByChain),
    // `null` and absent both mean "nobody has counted this entity", which the
    // caller must render as unknown rather than as zero.
    member_total: page?.memberTotal ?? null,
    lane: toLane(page?.lane),
    truncated: page?.truncated ?? false
  };
}
function toLane(value) {
  return String(value ?? "").toLowerCase() === "core" ? "core" : "all";
}
function mapPrimaryByChain(raw) {
  if (raw == null) return {};
  let source = raw;
  if (typeof raw === "string") {
    try {
      source = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (typeof source !== "object" || source === null || Array.isArray(source)) return {};
  const out = {};
  for (const [chain, address] of Object.entries(source)) {
    if (chain && typeof address === "string" && address) out[chain] = address;
  }
  return out;
}
async function graphqlPutEntityMemberships(client, args, opts) {
  const data = await client.request(
    PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION,
    {
      input: omitUndefined({
        entityId: args.entity_id,
        role: args.role,
        tier: toTierEnum(args.tier),
        confidence: args.confidence,
        members: args.members.map(
          (m) => omitUndefined({
            address: m.address,
            chainId: m.chain,
            role: m.role,
            tier: toTierEnum(m.tier),
            confidence: m.confidence,
            validFrom: m.valid_from,
            validTo: m.valid_to
          })
        )
      })
    },
    opts
  );
  const out = data.putAddressEntityMemberships;
  const results = (out?.results ?? []).map((r) => ({
    chain: r.chainId,
    address: r.address,
    ok: r.ok,
    mapping: mapEntityMapping(r.mapping),
    error: r.error ?? null
  }));
  return {
    results,
    count: out?.count ?? results.filter((r) => r.ok).length,
    failed: out?.failed ?? results.filter((r) => !r.ok).length,
    mappings: results.map((r) => r.mapping).filter((m) => m != null)
  };
}

export { ADDRESSES_QUERY, ADDRESS_ASSIGNMENTS_QUERY, ADDRESS_CROSS_CHAIN_QUERY, ADDRESS_DETAIL_QUERY, ADDRESS_ENTITY_MAPPING_QUERY, ADDRESS_OVERLAYS_QUERY, ADDRESS_ROW_FIELDS, ADDRESS_SCOPES, ASSIGNMENTS_PAGE_BUDGET, ASSIGN_ADDRESS_CATALOG_MUTATION, CATALOG_ITEMS_QUERY, CATALOG_ITEM_FIELDS, CATALOG_ITEM_QUERY, CATALOG_RELATED_QUERY, CLEAR_ADDRESS_PRIMARY_MUTATION, DELETE_ADDRESS_ENTITY_MAPPING_MUTATION, ENTITY_MEMBERSHIPS_QUERY, GRAPHQL_BATCH_REF_LIMIT, GRAPHQL_CATALOG_ID_LIMIT, GraphQLClient, GraphQLRequestError, LATEST_TRANSACTIONS_QUERY, PUT_ADDRESS_ENTITY_MAPPING_MUTATION, PUT_ADDRESS_ENTITY_MEMBERSHIPS_MUTATION, RELATED_SEED_KINDS, REQUEST_TRANSACTION_MUTATION, RESOLVED_ENTITY_FIELDS, RESOLVED_HIT_CORE_FIELDS, RESOLVED_HIT_FIELDS, SET_ADDRESS_PRIMARY_MUTATION, TRANSACTION_HEADER_QUERY, UNASSIGN_ADDRESS_CATALOG_MUTATION, addressBatchRequest, addressCrossChainIncomplete, addressListUnroutable, catalogItemsByIdRequest, catalogKindToGraphql, catalogListUnroutable, entityMembershipsUnroutable, fetchAddressOverlays, fetchCatalogItem, fetchCatalogItems, fetchCatalogRelated, fetchLatestTransactions, fetchTransactionHeader, graphqlAttachAssignment, graphqlClearPrimary, graphqlDeleteEntityMapping, graphqlDetachAssignment, graphqlFetchAddressCrossChain, graphqlFetchAddressDetail, graphqlFetchCatalogItemsByIds, graphqlGetEntityMapping, graphqlListAddresses, graphqlListAddressesWithOverlays, graphqlListAssignments, graphqlListEntityMemberships, graphqlListMode, graphqlPutEntityMapping, graphqlPutEntityMemberships, graphqlRequestTransaction, graphqlResolveAddressesBatch, graphqlSetPrimary, isGraphQLRequestError, isOpaqueRequestId, mapAddressCrossChain, mapAddressDetail, mapAddressListOverlays, mapAddressOverlays, mapAddressPage, mapAddressRow, mapAddressRowScope, mapAssignment, mapBundle, mapCatalogItemToRow, mapCatalogItems, mapCrossChainActivity, mapEntity, mapEntityMapping, mapLatestTransactions, mapPrimaryPointer, mapRelatedGraph, mapTransactionHeader, relatedChildren, relatedOfKind, resolvedBundleFields, toAddressScope, toCatalogKindEnum };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map